// Speech synthesis utility for reading addresses
class SpeechService {
  private speechSynthesis: SpeechSynthesis | null = null;
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voicesLoaded: boolean = false;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.speechSynthesis = window.speechSynthesis;
      
      // Load voices when they become available
      if (this.speechSynthesis.onvoiceschanged !== undefined) {
        this.speechSynthesis.onvoiceschanged = () => {
          this.voicesLoaded = true;
          console.log('Voices loaded:', this.speechSynthesis?.getVoices()?.length || 0);
        };
      }
      
      // Check if voices are already available
      if (this.speechSynthesis.getVoices().length > 0) {
        this.voicesLoaded = true;
      }
    }
  }

  isSpeechSupported(): boolean {
    return this.speechSynthesis !== null;
  }

  // Wait for voices to be loaded
  async waitForVoices(): Promise<void> {
    if (!this.speechSynthesis) {
      throw new Error('Speech synthesis not supported');
    }

    if (this.voicesLoaded) {
      return;
    }

    return new Promise((resolve) => {
      const checkVoices = () => {
        const voices = this.speechSynthesis?.getVoices();
        if (voices && voices.length > 0) {
          this.voicesLoaded = true;
          resolve();
        } else {
          setTimeout(checkVoices, 100);
        }
      };
      checkVoices();
    });
  }

  stop(): void {
    if (this.speechSynthesis && this.currentUtterance) {
      this.speechSynthesis.cancel();
      this.currentUtterance = null;
    }
  }

  // Get available voices for a specific language
  getVoicesForLanguage(language: string): SpeechSynthesisVoice[] {
    if (!this.speechSynthesis) return [];
    
    const voices = this.speechSynthesis.getVoices();
    return voices.filter(voice => 
      voice.lang.toLowerCase().startsWith(language.toLowerCase())
    );
  }

  // Get the best voice for Chinese speech synthesis
  getBestChineseVoice(): SpeechSynthesisVoice | null {
    if (!this.speechSynthesis) return null;
    
    const voices = this.speechSynthesis.getVoices();
    console.log('Available voices:', voices.map(v => `${v.name} (${v.lang})`));
    
    const chineseVoices = voices.filter(voice => 
      voice.lang.toLowerCase().includes('zh') || 
      voice.lang.toLowerCase().includes('cmn')
    );
    
    console.log('Chinese voices found:', chineseVoices.map(v => `${v.name} (${v.lang})`));
    
    // Prefer voices with better quality indicators
    const preferredVoices = chineseVoices.filter(voice => 
      voice.name.toLowerCase().includes('google') ||
      voice.name.toLowerCase().includes('microsoft') ||
      voice.name.toLowerCase().includes('siri')
    );
    
    const selectedVoice = preferredVoices.length > 0 ? preferredVoices[0] : chineseVoices[0] || null;
    console.log('Selected Chinese voice:', selectedVoice ? `${selectedVoice.name} (${selectedVoice.lang})` : 'None');
    
    return selectedVoice;
  }

  async speak(text: string, language: string, onEnd?: () => void, onError?: (error: Error) => void): Promise<void> {
    if (!this.speechSynthesis) {
      if (onError) {
        onError(new Error('Speech synthesis not supported'));
      }
      return;
    }

    // Wait for voices to be loaded
    await this.waitForVoices();

    // Stop any current speech
    this.stop();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language;
    utterance.rate = 0.8; // Slightly slower for better comprehension
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // For Chinese, try to select a better voice
    if (language.startsWith('zh') || language.startsWith('cmn')) {
      const chineseVoice = this.getBestChineseVoice();
      if (chineseVoice) {
        utterance.voice = chineseVoice;
        console.log('Using Chinese voice:', chineseVoice.name, chineseVoice.lang);
      }
    }

    utterance.onend = () => {
      this.currentUtterance = null;
      if (onEnd) onEnd();
    };

    utterance.onerror = (event) => {
      this.currentUtterance = null;
      console.error('Speech synthesis error:', event.error);
      if (onError) {
        onError(new Error(`Speech synthesis error: ${event.error}`));
      }
    };

    this.currentUtterance = utterance;
    this.speechSynthesis.speak(utterance);
  }
}

// Create a singleton instance
export const speechService = new SpeechService();

// Function to read address with proper formatting
export const readAddress = async (
  address: string, 
  name: string, 
  district?: string, 
  language: 'en' | 'zh' = 'en'
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!speechService.isSpeechSupported()) {
      reject(new Error('Speech synthesis not supported'));
      return;
    }

    let textToRead = '';
    let langCodes: string[] = [];

    if (language === 'zh') {
      // Chinese text formatting with multiple language code options
      langCodes = ['zh-CN', 'zh-TW', 'zh-HK', 'cmn-CN', 'cmn-TW'];
      
      if (district) {
        textToRead = `${name}，位于${district}，${address}`;
      } else {
        textToRead = `${name}，${address}`;
      }
    } else {
      // English text formatting
      langCodes = ['en-US', 'en-GB'];
      if (district) {
        textToRead = `${name}, located in ${district}, ${address}`;
      } else {
        textToRead = `${name}, ${address}`;
      }
    }

    console.log('Reading address:', textToRead, 'with language codes:', langCodes);

    // Try each language code until one works
    const trySpeak = (index: number) => {
      if (index >= langCodes.length) {
        reject(new Error('All language codes failed for speech synthesis'));
        return;
      }

      const currentLangCode = langCodes[index];
      console.log(`Trying language code: ${currentLangCode}`);

      speechService.speak(
        textToRead,
        currentLangCode,
        () => {
          console.log(`Successfully read with language code: ${currentLangCode}`);
          resolve();
        },
        (error) => {
          console.warn(`Failed with language code ${currentLangCode}:`, error);
          // Try next language code
          trySpeak(index + 1);
        }
      ).catch((error) => {
        console.warn(`Failed with language code ${currentLangCode}:`, error);
        // Try next language code
        trySpeak(index + 1);
      });
    };

    // Start with the first language code
    trySpeak(0);
  });
}; 